import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useAuth } from "../../../login/AuthProvider.js";
import "./EditPreferences.css";

const EditPreferences = ({
  preferences,
  onChange,
  onSubmit,
}) => {
  const { userId } = useParams();
  const { getAuthHeaders, getApiBaseUrl } = useAuth();

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [localPreferences, setLocalPreferences] = useState({
    teethNotation: "Universal",
    language: "English (US)",
    disableRxTyping: false,
    technicianStamp: "",
  });

  const languages = [
    "English (US)", "Spanish", "French", "German", "Chinese (Simplified)",
    "Arabic", "Hindi", "Russian", "Portuguese", "Japanese", "Bengali",
    "Urdu", "Indonesian", "Turkish", "Italian", "Thai", "Korean",
    "Vietnamese", "Tamil", "Filipino",
  ];

  useEffect(() => {
    if (preferences) {
      const stampPath = preferences.technicianStamp || "";

      setLocalPreferences({
        teethNotation: preferences.teethNotation || "Universal",
        language: preferences.language || "English (US)",
        disableRxTyping: preferences.disableRxTyping || false,
        technicianStamp: stampPath,
      });

      if (stampPath) {
        let cleanPath = stampPath;
        if (!cleanPath.startsWith("/")) {
          cleanPath = `/uploads/stamps/${cleanPath}`;
        } else if (!cleanPath.startsWith("/uploads")) {
           cleanPath = `/uploads/stamps/${cleanPath.replace(/^\//, '')}`;
        }
        const fullUrl = `${getApiBaseUrl()}${cleanPath}?t=${new Date().getTime()}`;
        setPreviewUrl(fullUrl);
      } else {
        setPreviewUrl("");
      }
    }
  }, [preferences, getApiBaseUrl]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updatedValue = type === "checkbox" ? checked : value;
    const updatedPreferences = { ...localPreferences, [name]: updatedValue };
    setLocalPreferences(updatedPreferences);
    onChange(updatedPreferences);
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
    } else {
      setFile(null);
      if (localPreferences.technicianStamp) {
        let cleanPath = localPreferences.technicianStamp;
        if (!cleanPath.startsWith("/uploads")) {
          cleanPath = `/uploads/stamps/${cleanPath}`;
        }
        setPreviewUrl(`${getApiBaseUrl()}${cleanPath}?t=${new Date().getTime()}`);
      } else {
        setPreviewUrl("");
      }
    }
  };

  const handleDeleteStamp = async () => {
    if (!localPreferences.technicianStamp) return;

    const confirmDelete = window.confirm(
      "Are you sure you want to delete your technician stamp? This action cannot be undone."
    );
    if (!confirmDelete) return;

    try {
      setDeleting(true);
      const headers = getAuthHeaders();
      const deleteResponse = await axios.delete(
        `${getApiBaseUrl()}/api/auth/delete-stamp/${userId}`,
        { headers, data: { stampPath: localPreferences.technicianStamp } }
      );

      if (deleteResponse.data.success) {
        const updatedPreferences = { ...localPreferences, technicianStamp: "" };
        setLocalPreferences(updatedPreferences);
        setPreviewUrl("");
        setFile(null);
        onChange(updatedPreferences);
        localStorage.removeItem("technicianStamp");
        window.dispatchEvent(new Event("stampUpdated"));
        
        if (onSubmit) await onSubmit({ preferences: updatedPreferences });
      } else {
        throw new Error(deleteResponse.data.message || "Failed to delete technician stamp");
      }
    } catch (error) {
      console.error("Error deleting stamp:", error);
      alert(error.response?.data?.message || "Failed to delete technician stamp");
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);

      if (file) {
        const formData = new FormData();
        formData.append("userId", userId);
        formData.append("technicianStamp", file);
        const headers = { ...getAuthHeaders(), "Content-Type": "multipart/form-data" };
        const uploadResponse = await axios.post(`${getApiBaseUrl()}/api/auth/upload-stamp`, formData, { headers });

        if (uploadResponse.data.success) {
          const technicianStampPath = uploadResponse.data.filePath;
          setFile(null);
          const fullUrl = `${getApiBaseUrl()}${technicianStampPath}?t=${new Date().getTime()}`;
          setPreviewUrl(fullUrl);
          const updatedPreferences = { ...localPreferences, technicianStamp: technicianStampPath };
          
          // Call parent onSubmit only
          await onSubmit({ preferences: updatedPreferences });
          
          setLocalPreferences(updatedPreferences);
          onChange(updatedPreferences);
          
          if (technicianStampPath) {
            localStorage.setItem("technicianStamp", technicianStampPath);
            window.dispatchEvent(new Event("stampUpdated"));
          }
        } else {
          throw new Error(uploadResponse.data.message || "Failed to upload technician stamp");
        }
      } else {
        const updatedPreferences = { ...localPreferences };
        
        // Call parent onSubmit only
        await onSubmit({ preferences: updatedPreferences });
        
        setLocalPreferences(updatedPreferences);
        onChange(updatedPreferences);
      }
    } catch (error) {
      console.error("Error updating preferences:", error);
      // Error show simple alert
      alert(error.response?.data?.message || "Failed to update preferences");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <div className="preferences-page-wrapper">
      <div className="row">
        <div className="col-md-10">
          <h1>Preferences</h1>
          <form onSubmit={handleSubmit}>
            <div className="preferences-main-box">
              <div className="preferences-content">
                <div>
                  <h4>Teeth Notation</h4>
                  <label><input type="radio" name="teethNotation" value="Universal" checked={localPreferences.teethNotation === "Universal"} onChange={handleInputChange} /> Universal</label>
                  <label><input type="radio" name="teethNotation" value="FDI" checked={localPreferences.teethNotation === "FDI"} onChange={handleInputChange} /> FDI</label>
                </div>
                <div className="preferences-section-spacing">
                  <h4>Language</h4>
                  <select className="preferences-language-select" name="language" value={localPreferences.language} onChange={handleInputChange}>
                    {languages.map((lang, index) => (<option key={index} value={lang}>{lang}</option>))}
                  </select>
                </div>
                <div className="preferences-section-spacing">
                  <h4>Technician's Stamp</h4>
                  <p className="preferences-description">Upload your technician stamp (PNG, JPEG, GIF)<br />Your stamp will be resized to 120px x 110px.</p>
                  <input className="preferences-file-input" type="file" onChange={handleFileChange} accept="image/png,image/jpeg,image/gif" disabled={uploading || deleting} />
                  <div className="preferences-stamp-content">
                    {previewUrl ? (
                      <div className="preferences-preview-wrapper">
                        <img className="preferences-stamp-image" src={previewUrl} alt="Technician Stamp Preview" onError={(e) => { e.target.onerror = null; e.target.src = ""; setPreviewUrl(""); }} />
                        {localPreferences.technicianStamp && !file && (<p className="preferences-current-stamp">✓ Current stamp: {localPreferences.technicianStamp.split("/").pop()}</p>)}
                        <button type="button" className="preferences-delete-button" onClick={handleDeleteStamp} disabled={uploading || deleting} style={{ backgroundColor: uploading || deleting ? "#e9ecef" : "#dc3545", color: uploading || deleting ? "#6c757d" : "white", border: "none", padding: "6px 12px", borderRadius: "4px", cursor: uploading || deleting ? "not-allowed" : "pointer", fontSize: "12px", fontWeight: "500", transition: "background-color 0.2s", width: "auto", display: "inline-block" }} onMouseEnter={(e) => { if (!uploading && !deleting) e.target.style.backgroundColor = "#c82333"; }} onMouseLeave={(e) => { if (!uploading && !deleting) e.target.style.backgroundColor = "#dc3545"; }}>
                          {deleting ? "Deleting..." : "🗑️ Delete Stamp"}
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div className="preferences-no-stamp">No Stamp Uploaded</div>
                        {localPreferences.technicianStamp && !file && (<p className="preferences-missing-stamp">⚠️ Stamp file not found</p>)}
                      </div>
                    )}
                  </div>
                  {file && !uploading && (<p className="preferences-status-file">📄 New file selected: {file.name}</p>)}
                  {uploading && <p className="preferences-status-uploading">⏳ Saving preferences...</p>}
                  {deleting && <p className="preferences-status-deleting">🗑️ Deleting stamp...</p>}
                </div>
                <div className="preferences-rx-option">
                  <input type="checkbox" name="disableRxTyping" checked={localPreferences.disableRxTyping} onChange={handleInputChange} disabled={uploading || deleting} /> Disable Rx Typing Suggestions
                </div>
                <div className="preferences-save-wrapper">
                  <button type="submit" className="preferences-save-button" style={{ backgroundColor: uploading || deleting ? "#ccc" : "#004aad", color: "#fff", border: "none", padding: "8px 16px", borderRadius: "4px", cursor: uploading || deleting ? "not-allowed" : "pointer", transition: "background-color 0.2s" }} disabled={uploading || deleting} onMouseEnter={(e) => { if (!uploading && !deleting) e.target.style.backgroundColor = "#003d8a"; }} onMouseLeave={(e) => { if (!uploading && !deleting) e.target.style.backgroundColor = "#004aad"; }}>
                    {uploading ? "💾 Saving..." : "💾 Save Preferences"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditPreferences;